import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { CheckCircle2, Package, ShoppingCart, Tag } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CartWithStats } from '../../models';

interface CartsListProps {
  carts: CartWithStats[];
  isLoading?: boolean;
}

export const CartsList: React.FC<CartsListProps> = ({ carts, isLoading = false }) => {
  const { t } = useTranslation('reusedModule');
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">{t('loading.carts', 'Cargando carritos...')}</p>
        </div>
      </div>
    );
  }

  if (carts.length === 0) {
    return (
      <div className="flex items-center justify-center max-h-64 bg-muted/30 rounded-xl border border-dashed border-border">
        <div className="text-center p-8">
          <ShoppingCart className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('empty.carts_title', 'No hay carritos utilizados')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('empty.carts_desc', 'Los carritos que uses en esta recepción aparecerán aquí')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">{t('card.used_carts', 'Carritos utilizados')}</h3>
        <span className="text-sm text-muted-foreground">
          {carts.length} {t('card.cart', 'carrito')}
          {carts.length !== 1 ? 's' : ''}
        </span>
      </div>

      <ScrollArea className="h-[calc(100vh-28rem)]">
        <div className="space-y-3">
          {carts.map((cart) => (
            <div
              key={cart.id}
              className="border rounded-lg p-3 transition-colors bg-background border-border hover:border-primary/30"
            >
              <div className="flex items-center justify-between gap-3">
                {/* Info del carrito */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                    <span className="font-mono font-semibold text-foreground">{cart.code}</span>
                  </div>

                  {/* Estadísticas */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {cart.total_products} {t('card.product', 'producto')}
                      {cart.total_products !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {cart.total_quantity} ud.
                    </span>
                  </div>
                </div>

                {/* Estado de ubicación */}
                <div className="flex-shrink-0 text-right">
                  {cart.has_location_order && cart.location_order_id ? (
                    <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-xs font-medium">
                        {t('card.location_order', 'Orden de ubicación')} #{cart.location_order_id}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t('status.to_locate', 'Por ubicar')}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
